import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { PredefinedCity, SearchHotelModel } from 'ts/hotel/classes/SearchHotelModel';
import DaterangePicker from 'components/ui/DaterangePicker/DaterangePicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { Select } from 'ts/interfaces/Generic';
import Rating from 'components/ui/Rating/Rating';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import AutocompleteCities from 'components/ui/AutocompleteCities/AutocompleteCities';
import Button from 'components/ui/Button/Button';
import {
  getGooglePrediction,
  getGooglePredictionDetails,
  getSearchFilter,
  saveUserConfig,
} from 'services/hotel';
import { SearchFilterModel } from 'ts/hotel/interfaces/SearchFilter';
import RoomsDropdown from './RoomsDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faGlobe,
  faLocationDot,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { onClickClose } from 'utils/responsive';
import { showError } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import { faCircleDot, faStar, faUser } from '@fortawesome/free-regular-svg-icons';
import { getHotelIndex, getKey, getNationality } from 'utils/hotel';
import { faChevronsDown, faChevronsUp } from '@fortawesome/sharp-duotone-solid-svg-icons';
import useRecaptcha from 'hooks/useRecaptcha';
import * as idb from 'utils/shared/idbUrl';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b } from 'constants/generic';

interface SearchHotelProps {
  loadingSearch: boolean;
  onClickStop: () => void;
  predefined?: PredefinedCity;
  b2bPathname?: string;
}

const SearchHotel: FC<SearchHotelProps> = (props) => {
  const { hotelIndex } = useContext(HotelConfigContext);
  const { user, setUserSiteConfig, userSiteConfig, initSiteData } = useContext(MainContext);
  const { handleRecapcha } = useRecaptcha();
  const [searchParams, setSearchParams] = useState(
    new SearchHotelModel(undefined, props.predefined, b2b)
  );
  const [searchFilter, setSearchFilter] = useState<SearchFilterModel>();
  const [radiuses, setRadiuses] = useState<Select[]>([]);
  const [starRating, setStarRating] = useState<Select[]>([]);
  const [nationality, setNationality] = useState<Select[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [searchUrlParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation();
  const [selectedCity, setSelectedCity] = useState(searchUrlParams.get('city') || '');

  const isResultPage = location.pathname.includes('results');
  const [isShortMode, setIsShortMode] = useState(isResultPage);
  const [googleAutocomplete, setGoogleAutocomplete] = useState<any>();
  const hotelDaterangeDefault = useRef(0);

  useEffect(() => {
    if (initSiteData?.other?.hotelDaterangeDefault) {
      hotelDaterangeDefault.current = initSiteData.other.hotelDaterangeDefault === 'true' ? 1 : -1;
    } else {
      hotelDaterangeDefault.current = -1;
    }

    (async () => {
      const newFilter = await getSearchFilter();

      setSearchFilter(newFilter);

      const newRadiuses =
        newFilter?.radiusList
          .map((item) => ({
            value: parseFloat(item.value),
            label: item.text,
          }))
          .sort((a, b) => a.value - b.value) || [];
      setRadiuses(newRadiuses);

      const newRatings =
        newFilter?.starRatingList.map((item) => ({
          value: parseFloat(item.value),
          label: item.text,
          html: (
            <div className="display-flex">
              <Rating value={item.value} className="mr-10" />
              {item.text}
            </div>
          ),
        })) || [];
      setStarRating(newRatings);

      const newNationality =
        newFilter?.nationalityList.map((item) => ({
          value: item.value,
          label: item.text,
        })) || [];
      setNationality(newNationality);
      setIsSubmitted(false);

      if (location.pathname.includes('results')) {
        if (searchUrlParams.get('checkIn') && location.pathname.includes((hotelIndex || '') + '')) {
          const searchParams = new SearchHotelModel(searchUrlParams);
          setSearchParams(searchParams);
        }

        setIsShortMode(true);

        const gaString = sessionStorage.getItem(getKey('hotelSearchGA', hotelIndex));
        if (gaString) {
          setGoogleAutocomplete(JSON.parse(gaString));
        }
      } else {
        setIsShortMode(false);
        const newSearchParams = new SearchHotelModel(undefined, undefined, b2b);
        setSelectedCity('');
        const nationality = getNationality(user);
        setSearchParams({
          ...newSearchParams,
          searchRadius: userSiteConfig.hotel.searchRadius ? userSiteConfig.hotel.searchRadius : 1,
          starRating: userSiteConfig.hotel.starRating ?? searchFilter?.starRating ?? 3,
          nationality,
        });
      }
    })();
  }, [location.pathname]); // eslint-disable-line

  // The results URL can change without leaving the page (the AI assistant opens another search this way), and
  // the effect on location.pathname above does not run then: keep the search bar in step with the URL instead
  // of leaving it on the previous search. Only this instance's own results route, as Hotels 1 and 2 coexist.
  useEffect(() => {
    if (
      location.pathname !== `/hotels${getHotelIndex(hotelIndex)}/results` ||
      !searchUrlParams.get('checkIn')
    ) {
      return;
    }

    setSearchParams(new SearchHotelModel(searchUrlParams));

    const gaString = sessionStorage.getItem(getKey('hotelSearchGA', hotelIndex));
    if (gaString) {
      setGoogleAutocomplete(JSON.parse(gaString));
    }
  }, [searchUrlParams]); // eslint-disable-line

  const onClickSearch = async () => {
    setIsSubmitted(true);

    if (isResultPage) {
      setIsShortMode(true);
    }

    await idb.deletePageStateByPartialKey('hotels');
    if (searchParams.city) {
      const path = `/hotels${getHotelIndex(hotelIndex)}/results`;

      let newGoogleAutocomplete: any = googleAutocomplete;
      if (!googleAutocomplete) {
        const predictions = await getGooglePrediction(
          { query: searchParams.city, prefix: 'htl' },
          new AbortController().signal
        );
        if (predictions?.length) {
          newGoogleAutocomplete = await getGooglePredictionDetails(predictions[0].place_id);
        }
      }

      const city = newGoogleAutocomplete.address_components?.find((item: any) => {
        const cityTypes = ['locality', 'postal_town', 'administrative_area_level_2'];
        return cityTypes.some((type) => item.types.includes(type));
      });

      const country = newGoogleAutocomplete.address_components?.find((item: any) =>
        item.types.includes('country')
      );

      const searchParamsStr = new URLSearchParams({
        ...searchParams,
        searchRadius: b2b ? searchParams.searchRadius : '1',
        loc: `${city ? city?.long_name : ''}, ${country ? country?.long_name : ''}`,
      } as unknown as Record<string, string>).toString();

      const element = document.querySelector('.search-wrapper') as HTMLElement;
      element?.classList.remove('visible-responsive');

      if (searchParams.id) {
        const newSearchParams = searchParamsStr + '&refreshDate=' + new Date().getTime();
        navigate({ pathname: path, search: newSearchParams });
      } else {
        showError(t('hotel.sorry_we_cant_find_hotel_here'));
      }
    }
  };

  const onClickStop = () => {
    setIsSubmitted(false);
    props.onClickStop();
  };

  const onChangeRadius = async (searchRadius: number) => {
    setSearchParams({ ...searchParams, searchRadius });

    const newUserSiteConfig = {
      ...userSiteConfig,
      hotel: {
        ...userSiteConfig.hotel,
        searchRadius,
      },
    };
    setUserSiteConfig(newUserSiteConfig);

    const recaptchaToken = await handleRecapcha();

    await saveUserConfig({
      recaptchaToken,
      input: newUserSiteConfig,
    });
  };

  const hideOverlay = (e?: any) => {
    if (e?.target.classList.contains('visible-responsive')) {
      e.target.classList.remove('visible-responsive');

      const hmain: any = document.querySelector('.hmain' + location.pathname.replace(/\D/g, ''));
      if (hmain) {
        hmain.style.display = 'none';
      }
    } else if (!e) {
      const hmain: any = document.querySelector('.hmain' + location.pathname.replace(/\D/g, ''));
      if (hmain) {
        hmain.style.display = 'none';
      }
    }
  };

  const onClickNewSearch = () => {
    navigate('/hotels' + getHotelIndex(hotelIndex));
    idb.deletePageStateByPartialKey('/hotels' + getHotelIndex(hotelIndex));
    setTimeout(() => {
      const latestPageUrl = localStorage.getItem('latestPageUrl');
      if (latestPageUrl) {
        const latestPageUrlObj = JSON.parse(latestPageUrl);
        localStorage.setItem('latestPageUrl', JSON.stringify({ ...latestPageUrlObj, hotel: [] }));
      }
    }, 300);
  };

  return (
    <div
      className={
        'search-wrapper hotel-search-wr' +
        (!isShortMode && b2b && window.innerWidth > 768 ? ' hotel-search-wr-short' : '')
      }
      data-assistant-target="search"
      onClick={hideOverlay}
    >
      <div className="search-inner" style={{ zIndex: 24 }}>
        <div className="results-responsive-header">
          <span className="title text-md">{t('search.search')}</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        {isResultPage && (b2b || userSiteConfig.base.rememberModeEnabled) && (
          <div className="search-element radius">
            <Button
              title={t('home.new_search')}
              onClick={onClickNewSearch}
              className="btn btn-default"
            />
          </div>
        )}
        {b2b && (
          <div className="search-element radius">
            <SelectCustom
              options={radiuses}
              value={searchParams.searchRadius}
              onChange={onChangeRadius}
              movablePlaceholder={t('search.radius')}
              movablePlaceholderIcon={faCircleDot}
            />
          </div>
        )}

        <div className="search-element locations">
          <AutocompleteCities
            key={`${searchUrlParams.get('city') || ''}-${searchUrlParams.get('lat') || ''}-${searchUrlParams.get('lon') || ''}`}
            value={selectedCity}
            defaultCity={props.predefined?.city}
            isSubmitted={isSubmitted}
            sessionStorageKey={getKey('hotelSearchGA', hotelIndex)}
            onSelect={(dest) => {
              setSelectedCity(dest.placeId);
              setGoogleAutocomplete(dest.fullValue);
              setSearchParams((prev) => ({
                ...prev,
                lon: dest.lng,
                lat: dest.lat,
                city: dest.city,
                id: dest.id,
                selectedHotelName: dest.types.includes('establishment') ? dest.locationName : '',
              }));
            }}
            movablePlaceholder={t('search.destination')}
            movablePlaceholderIcon={faLocationDot}
          />
        </div>
        {!b2b && (
          <div className="search-element datepicker">
            <DaterangePicker
              onChange={(value) =>
                setSearchParams({
                  ...searchParams,
                  checkIn: value.startDate,
                  checkOut: value.endDate,
                })
              }
              value={{ startDate: searchParams.checkIn, endDate: searchParams.checkOut }}
              movablePlaceholder={t('hotel.dates')}
              movablePlaceholderIcon={faCalendar}
              scrollOnFocusMobile
              showNights
            />
          </div>
        )}
        {b2b && (
          <div className="search-element datepicker-vertical">
            <DaterangePicker
              onChange={(value) =>
                setSearchParams({
                  ...searchParams,
                  checkIn: value.startDate,
                  checkOut: value.endDate,
                })
              }
              value={{ startDate: searchParams.checkIn, endDate: searchParams.checkOut }}
              scrollOnFocusMobile
              vertical={true}
              showNights
            />
          </div>
        )}

        <div className="search-element rooms">
          <RoomsDropdown
            onChange={(value) => {
              setSearchParams((prev) => ({ ...prev, roomsString: value }));
            }}
            adultsList={searchFilter?.adultsList}
            childrenList={searchFilter?.childrenList}
            agesList={searchFilter?.agesList}
            movablePlaceholder={t('search.rooms')}
            movablePlaceholderIcon={faUser}
          />
        </div>
        {b2b && (
          <div className="search-element star">
            <SelectCustom
              options={starRating}
              value={searchParams.starRating}
              onChange={(starRating) => setSearchParams({ ...searchParams, starRating })}
              movablePlaceholder={t('hotel.stars')}
              movablePlaceholderIcon={faStar}
              movablePlaceholderAlwaysSmall={true}
              showHtmlText
            />
          </div>
        )}
        {b2b && isResultPage && (
          <div className="search-button hide-laptop-sm" style={{ width: 'auto' }}>
            <button
              style={{ width: 28, padding: '0 3px', marginBottom: isShortMode ? 0 : 3 }}
              className="btn btn-default"
              onClick={() => setIsShortMode((prev) => !prev)}
            >
              <FontAwesomeIcon icon={(isShortMode ? faChevronsDown : faChevronsUp) as any} />
            </button>
          </div>
        )}
        <div className="search-button " style={{ marginTop: 0 }}>
          {!props.loadingSearch && (
            <button
              className="btn btn-primary btn-go"
              onClick={() => {
                onClickSearch();
                hideOverlay();
              }}
            >
              GO
            </button>
          )}
          {props.loadingSearch && (
            <button className="btn btn-active" onClick={onClickStop}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        {b2b && !isShortMode && <div className="search-row-break hide-laptop-sm" aria-hidden />}
        {b2b && !isShortMode && (
          <div className="search-element nationality hide-laptop-sm">
            <SelectCustom
              options={nationality}
              value={searchParams.nationality}
              onChange={(nationality) => setSearchParams({ ...searchParams, nationality })}
              movablePlaceholder={t('search.nationality')}
              movablePlaceholderIcon={faGlobe}
            />
          </div>
        )}
        {b2b && !isShortMode && user?.branchId === 5000 && (
          <div className="search-element checkbox hide-laptop-sm">
            <FormGroup
              value={searchParams.suppliers}
              onChange={(suppliers) => setSearchParams({ ...searchParams, suppliers })}
              movablePlaceholder={t('search.suppliers')}
              movablePlaceholderIcon={faGlobe}
              label={t('search.suppliers')}
            />
          </div>
        )}
        {b2b && !isShortMode && (
          <div className="search-element checkbox flex-center hide-laptop-sm">
            <Checkbox
              label={t('search.display_exactly')}
              checked={searchParams.displayExactly}
              onChange={(displayExactly) => setSearchParams({ ...searchParams, displayExactly })}
            />
          </div>
        )}
        {b2b && !isShortMode && (
          <div className="search-element checkbox flex-center hide-laptop-sm">
            <Checkbox
              label={t('filter.free_cancellation')}
              checked={searchParams.freeCancellation}
              onChange={(freeCancellation) =>
                setSearchParams({ ...searchParams, freeCancellation })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default withErrorBoundary(SearchHotel, 'SearchHotel');
